// Part of the agent UI for spark#246 / @adr SPARK-0034-ai-agents-are-project-context-not-team-members@1: an AI agent is PROJECT CONTEXT, never a team
// member. Nothing here reaches the solver, and no agent carries a Powers profile — Powers is a
// psychometric instrument built for humans, and putting agents on that scale would argue they are
// interchangeable with candidates, which is the model @adr SPARK-0034-ai-agents-are-project-context-not-team-members@1 rejects.

import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Modal } from "@/components/design/Modal";
import { BoxHeader } from "@/components/design/BoxHeader";
import { useUiStore } from "@/stores/ui-store";
import { AgentDetail } from "./AgentDetail";
import { AgentRow } from "./AgentRow";
import { displayAgent, findAgent } from "./agent-catalogue";
import type { MatchAgent } from "./design-codec";

/**
 * Result-step surface: the agents this project runs with, shown so the outcome reads with the full
 * context. Explicitly labelled as context — the roster above it stays persons-only.
 */
export function SelectedAgentsPanel({ agents }: { agents: MatchAgent[] }) {
  const locale = useUiStore((s) => s.locale);
  const [detailId, setDetailId] = useState<string | null>(null);
  const detail = detailId ? findAgent(detailId) : null;
  if (agents.length === 0) return null;
  return (
    <div className="box bg-surface p-5">
      <BoxHeader title={<FormattedMessage id="match.result.agentsTitle" defaultMessage="Agenten" />} />
      <p className="mb-4 max-w-xl text-[12px] text-ink/55">
        <FormattedMessage
          id="match.result.agentsIntro"
          defaultMessage="Mit diesen KI-Agenten arbeitet das Projekt. Sie sind Kontext und kein Teammitglied: das Team oben besteht ausschließlich aus Personen."
        />
      </p>
      <div className="grid grid-cols-1 gap-3 @sm:grid-cols-2">
        {agents.map((a) => (
          <AgentRow
            key={a.id}
            {...displayAgent(a, locale)}
            // Undefined for an agent whose catalogue entry is gone: the row still renders from the
            // stored snapshot, but there is no sheet, so it must not offer one.
            onOpen={findAgent(a.id) ? () => setDetailId(a.id) : undefined}
          />
        ))}
      </div>
      <Modal
        open={detail != null}
        onClose={() => setDetailId(null)}
        maxWidth="46rem"
        ariaLabel={detail?.name}
      >
        {detail ? <AgentDetail entry={detail} /> : null}
      </Modal>
    </div>
  );
}
